import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, setToken } from '../lib/api';
import {
  Badge,
  Button,
  Callout,
  Card,
  CopyableValue,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Table,
  Tabs,
  Td,
  Toggle,
} from '../components/ui/primitives';
import { relative, titleCase } from '../lib/format';
import { useAuth } from '../lib/auth';

interface BotPolicy {
  botScoreThreshold: number;
  suspectScoreThreshold: number;
  instantOpenSeconds: number;
  instantClickSeconds: number;
  burstLinkCount: number;
  burstWindowSeconds: number;
  ipVelocityMessages: number;
  ipVelocityWindowMinutes: number;
  treatMachineOpensAsHuman: boolean;
  excludeSuspectedFromFiltered: boolean;
  disabledRules: string[];
}

interface PolicyResponse {
  policy: BotPolicy;
  defaults: BotPolicy;
  rules: Array<{ id: string; label: string; severity: string; weight: number; description: string; enabled: boolean }>;
}

interface IntegrationsResponse {
  integrations: Array<{
    id: string;
    kind: string;
    provider: string;
    name: string;
    enabled: boolean;
    status: string;
    config: Record<string, string>;
    lastError: string | null;
  }>;
  catalogue: Record<
    string,
    Array<{ key: string; displayName: string; requires: string[]; optional?: string[] }>
  >;
}

export function SettingsPage() {
  const [tab, setTab] = useState('account');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <>
      <PageHeader
        title="Settings"
        description="Filtering policy, provider connections and the email infrastructure accounts the platform sends through."
      />
      <Tabs
        tabs={[
          { id: 'account', label: 'Your account' },
          ...(isAdmin ? [{ id: 'team', label: 'Team' }] : []),
          { id: 'filtering', label: 'Bot filtering policy' },
          { id: 'integrations', label: 'Integrations' },
          { id: 'esp', label: 'Sending providers' },
          { id: 'tracking', label: 'Conversion tracking' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'account' && <AccountPanel />}
      {tab === 'team' && isAdmin && <TeamPanel />}
      {tab === 'filtering' && <FilteringPanel />}
      {tab === 'integrations' && <IntegrationsPanel />}
      {tab === 'esp' && <EspPanel />}
      {tab === 'tracking' && <TrackingPanel />}
    </>
  );
}

interface Providers {
  microsoft: boolean;
  password: boolean;
}

const useProviders = () =>
  useQuery({ queryKey: ['auth-providers'], queryFn: () => api.get<Providers>('/api/auth/providers') });

/** Change your own password - or, under Microsoft sign-in, say where that lives now. */
function AccountPanel() {
  const providers = useProviders();
  if (providers.isLoading) return <Loading />;
  if (providers.data?.microsoft) {
    return (
      <div className="max-w-xl">
        <Callout tone="info" title="You sign in with Microsoft">
          Your password, MFA and session policies are managed in your organisation's Microsoft
          account. Password sign-in to EmailBlast is turned off.
        </Callout>
      </div>
    );
  }
  return <PasswordPanel />;
}

function PasswordPanel() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [result, setResult] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 12;
  const ready = current.length > 0 && next.length >= 12 && next === confirm;

  const change = useMutation({
    mutationFn: () =>
      api.post<{ token: string; message: string }>('/api/auth/change-password', {
        currentPassword: current,
        newPassword: next,
      }),
    onSuccess: (r) => {
      // The server issues a replacement token, so this session survives its
      // own change while every other session is ended.
      setToken(r.token);
      setCurrent('');
      setNext('');
      setConfirm('');
      setResult({ tone: 'good', text: r.message });
    },
    onError: (err: Error) => setResult({ tone: 'bad', text: err.message }),
  });

  return (
    <div className="max-w-xl space-y-5">
      <Card
        title="Change your password"
        subtitle="Changing it signs out every other session immediately, including any you no longer control."
      >
        <div className="space-y-4">
          <Field label="Current password">
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field label="New password" hint="At least 12 characters. A long passphrase beats a short complex one.">
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>

          {tooShort && <Callout tone="warn">Needs at least 12 characters.</Callout>}
          {mismatch && <Callout tone="warn">The two new passwords do not match.</Callout>}
          {result && <Callout tone={result.tone}>{result.text}</Callout>}

          <Button variant="primary" loading={change.isPending} disabled={!ready} onClick={() => change.mutate()}>
            Change password
          </Button>
        </div>
      </Card>

      <Callout tone="info" title="If you are locked out">
        An admin can reset another user's password from the API. If no admin can sign in, the
        bootstrap value in the server environment file is still the fallback.
      </Callout>
    </div>
  );
}

interface TeamUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'operator' | 'analyst';
  lastLoginAt: string | null;
  microsoftLinked: boolean;
}

/** Admins add people here; under Microsoft sign-in nobody gets in without an entry. */
function TeamPanel() {
  const queryClient = useQueryClient();
  const providers = useProviders();
  const microsoft = providers.data?.microsoft ?? false;
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: TeamUser[] }>('/api/users'),
  });

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<TeamUser['role']>('operator');
  const [password, setPassword] = useState('');
  const [result, setResult] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);

  const add = useMutation({
    mutationFn: () =>
      api.post<TeamUser>('/api/users', {
        email,
        name,
        role,
        ...(microsoft ? {} : { password }),
      }),
    onSuccess: (u) => {
      setEmail('');
      setName('');
      setPassword('');
      setResult({
        tone: 'good',
        text: microsoft
          ? u.email + ' added. They can now sign in with their Microsoft account.'
          : u.email + ' added.',
      });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: Error) => setResult({ tone: 'bad', text: err.message }),
  });

  const ready = email.includes('@') && name.trim().length > 0 && (microsoft || password.length >= 12);

  return (
    <div className="space-y-5">
      <Card title="Team members">
        {users.isLoading ? (
          <Loading />
        ) : (
          <Table head={['Name', 'Email', 'Role', ...(microsoft ? ['Microsoft'] : []), 'Last sign-in']}>
            {(users.data?.users ?? []).map((u) => (
              <tr key={u.id}>
                <Td>{u.name || '-'}</Td>
                <Td>{u.email}</Td>
                <Td>
                  <Badge>{titleCase(u.role)}</Badge>
                </Td>
                {microsoft && (
                  <Td>
                    {u.microsoftLinked ? (
                      <Badge tone="good">Linked</Badge>
                    ) : (
                      <Badge tone="neutral">Links on first sign-in</Badge>
                    )}
                  </Td>
                )}
                <Td>{u.lastLoginAt ? relative(u.lastLoginAt) : 'Never'}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card
        title="Add a team member"
        subtitle={
          microsoft
            ? 'Use the email address of their Microsoft work account. The first time they sign in, the account is linked to them permanently.'
            : undefined
        }
      >
        <div className="max-w-xl space-y-4">
          <Field label="Email">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Role" hint="Analysts can view everything but change nothing.">
            <Select value={role} onChange={(e) => setRole(e.target.value as TeamUser['role'])}>
              <option value="analyst">Analyst</option>
              <option value="operator">Operator</option>
              <option value="admin">Admin</option>
            </Select>
          </Field>
          {!microsoft && (
            <Field label="Initial password" hint="At least 12 characters. Share it with them out of band.">
              <Input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}

          {result && <Callout tone={result.tone}>{result.text}</Callout>}

          <Button variant="primary" loading={add.isPending} disabled={!ready} onClick={() => add.mutate()}>
            Add team member
          </Button>
        </div>
      </Card>
    </div>
  );
}

function FilteringPanel() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BotPolicy | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['bot-policy'],
    queryFn: () => api.get<PolicyResponse>('/api/settings/bot-policy'),
  });

  useEffect(() => {
    if (data && !draft) setDraft(data.policy);
  }, [data, draft]);

  const save = useMutation({
    mutationFn: () => api.put('/api/settings/bot-policy', { name: 'Default filtering policy', ...draft }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['bot-policy'] });
    },
  });

  if (isLoading || !data || !draft) return <Loading />;

  const toggleRule = (id: string) => {
    setDraft({
      ...draft,
      disabledRules: draft.disabledRules.includes(id)
        ? draft.disabledRules.filter((r) => r !== id)
        : [...draft.disabledRules, id],
    });
  };

  const numberField = (key: keyof BotPolicy, label: string, hint: string) => (
    <Field label={label} hint={hint}>
      <Input
        type="number"
        value={String(draft[key])}
        onChange={(e) => setDraft({ ...draft, [key]: Number(e.target.value) })}
      />
    </Field>
  );

  return (
    <div className="space-y-5">
      {saved && (
        <Callout tone="good">
          Policy saved. New events use it immediately; to apply it to history, re-run the bot filter
          from a campaign page.
        </Callout>
      )}

      <Card
        title="Thresholds"
        subtitle="Each rule contributes a weight to a 0-100 score. These thresholds decide what that score means."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {numberField('botScoreThreshold', 'Bot threshold', 'Score at or above which a hit is classified as a bot.')}
          {numberField(
            'suspectScoreThreshold',
            'Suspected threshold',
            'Score at or above which a hit is treated as suspected automation.'
          )}
          {numberField('instantOpenSeconds', 'Instant open (s)', 'Opens this soon after delivery are pre-fetches.')}
          {numberField('instantClickSeconds', 'Instant click (s)', 'Clicks this soon after delivery are detonations.')}
          {numberField('burstLinkCount', 'Burst link count', 'Distinct links in the window that imply a link checker.')}
          {numberField('burstWindowSeconds', 'Burst window (s)', 'Window the burst rule looks at.')}
          {numberField('ipVelocityMessages', 'IP velocity', 'Distinct recipients one address may touch before it is a gateway.')}
          {numberField('ipVelocityWindowMinutes', 'Velocity window (min)', 'Window for the velocity rule.')}
        </div>

        <div className="mt-5 space-y-3 border-t border-ink-100 pt-5">
          <Toggle
            checked={draft.treatMachineOpensAsHuman}
            onChange={(v) => setDraft({ ...draft, treatMachineOpensAsHuman: v })}
            label="Count Apple Mail Privacy Protection pre-fetches as human opens (not recommended)"
          />
          <Toggle
            checked={draft.excludeSuspectedFromFiltered}
            onChange={(v) => setDraft({ ...draft, excludeSuspectedFromFiltered: v })}
            label="Exclude suspected bots from filtered metrics"
          />
        </div>

        <div className="mt-5">
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save policy
          </Button>
        </div>
      </Card>

      <Card
        title="Detection rules"
        subtitle="Turn an individual signal off without a code change. Disabled rules stop contributing to the score immediately."
      >
        <Table head={['Enabled', 'Rule', 'Severity', 'Weight', 'What it detects']}>
          {data.rules.map((rule) => (
            <tr key={rule.id}>
              <Td>
                <input
                  type="checkbox"
                  checked={!draft.disabledRules.includes(rule.id)}
                  onChange={() => toggleRule(rule.id)}
                />
              </Td>
              <Td>
                <div className="font-medium text-ink-900">{rule.label}</div>
                <code className="text-[11px] text-ink-400">{rule.id}</code>
              </Td>
              <Td>
                <Badge
                  tone={
                    rule.severity === 'hard'
                      ? 'bad'
                      : rule.severity === 'strong'
                        ? 'warn'
                        : rule.severity === 'weak'
                          ? 'neutral'
                          : 'info'
                  }
                >
                  {rule.severity}
                </Badge>
              </Td>
              <Td className="tabular-nums">{rule.weight}</Td>
              <Td className="max-w-2xl text-xs leading-relaxed text-ink-500">{rule.description}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  );
}

/** Plain-language help for credential fields that are not self-explanatory. */
const FIELD_HINTS: Record<string, string> = {
  host: 'IMAP server, e.g. imap.gmail.com or outlook.office365.com',
  user: 'Full mailbox address',
  password: 'App password, not the account password, wherever the provider offers one',
  port: 'Default 993',
  secure: 'Default true. Set to false only for a server that requires STARTTLS on 143',
  folder: 'Default INBOX. Set this if a rule files the reports elsewhere',
  markSeen: 'Default true. Set to false to leave the mailbox untouched',
};

const isSecret = (key: string): boolean => {
  const k = key.toLowerCase();
  return k.includes('secret') || k.includes('password') || (k.includes('key') && k !== 'markseen');
};

function IntegrationsPanel() {
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState<{ kind: string; provider: string; name: string } | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get<IntegrationsResponse>('/api/settings/integrations'),
  });

  const disconnect = useMutation({
    mutationFn: (i: { kind: string; provider: string }) =>
      api.delete<{ ok: boolean; message: string }>(
        '/api/settings/integrations/' + i.kind + '/' + i.provider
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  });

  const save = useMutation({
    mutationFn: () =>
      api.put('/api/settings/integrations', {
        kind: edit?.kind,
        provider: edit?.provider,
        name: edit?.name,
        config: values,
        enabled: true,
      }),
    onSuccess: () => {
      setEdit(null);
      setValues({});
      setTestResult(null);
      void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  /**
   * Checks stored credentials against the live mailbox. Only the DMARC
   * reporting mailbox supports this today - it is the one integration whose
   * failure is otherwise silent, because a mailbox that cannot be opened
   * looks exactly like a mailbox with no reports in it.
   */
  const testMailbox = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; error: string | null; waiting: number | null }>(
        '/api/dmarc/mailbox-test',
        {}
      ),
    onSuccess: (r) =>
      setTestResult(
        r.ok
          ? {
              ok: true,
              message:
                'Connected. ' +
                (r.waiting === null
                  ? 'The mailbox opened.'
                  : r.waiting + ' unread message(s) waiting to be collected.'),
            }
          : { ok: false, message: r.error ?? 'Could not connect.' }
      ),
    onError: (err: Error) => setTestResult({ ok: false, message: err.message }),
  });

  if (isLoading || !data) return <Loading />;

  const entry =
    edit && data.catalogue[edit.kind]
      ? data.catalogue[edit.kind].find((p) => p.key === edit.provider)
      : undefined;
  const requirements = entry?.requires ?? [];
  // Rendered too, so a non-standard port or a folder other than INBOX can
  // actually be set rather than silently taking the default.
  const optionalFields = entry?.optional ?? [];

  return (
    <div className="space-y-5">
      <Card
        title="Connected services"
        subtitle="Everything is optional. Where no credential is present the platform falls back to its built-in equivalent rather than failing."
      >
        <Table head={['Type', 'Provider', 'Status', 'Enabled', 'Configuration', '']}>
          {data.integrations.map((i) => (
            <tr key={i.id}>
              <Td className="capitalize">{titleCase(i.kind)}</Td>
              <Td className="font-medium text-ink-900">{i.name}</Td>
              <Td>
                <Badge tone={i.status === 'configured' ? 'good' : i.status === 'error' ? 'bad' : 'neutral'}>
                  {i.status}
                </Badge>
                {i.lastError && <div className="mt-1 text-[11px] text-red-600">{i.lastError}</div>}
              </Td>
              <Td>{i.enabled ? <Badge tone="good">on</Badge> : <Badge>off</Badge>}</Td>
              <Td className="font-mono text-[11px] text-ink-500">
                {Object.entries(i.config)
                  .map(([k, v]) => k + '=' + (v || 'unset'))
                  .join('  ') || '--'}
              </Td>
              <Td>
                <div className="flex gap-1.5">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEdit({ kind: i.kind, provider: i.provider, name: i.name });
                      setValues({});
                    }}
                  >
                    Configure
                  </Button>
                  {i.status === 'configured' && (
                    <Button
                      variant="danger"
                      loading={disconnect.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            'Disconnect "' +
                              i.name +
                              '" and erase its stored credentials?\n\nThe platform falls back to its built-in equivalent.'
                          )
                        ) {
                          disconnect.mutate({ kind: i.kind, provider: i.provider });
                        }
                      }}
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      </Card>

      {edit && (
        <Card title={'Configure ' + edit.name}>
          {requirements.length === 0 && optionalFields.length === 0 ? (
            <Callout tone="info">This provider needs no credentials - it is built in and always available.</Callout>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {requirements.map((key) => (
                  <Field key={key} label={key} hint={FIELD_HINTS[key]}>
                    <Input
                      type={isSecret(key) ? 'password' : 'text'}
                      value={values[key] ?? ''}
                      onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                    />
                  </Field>
                ))}
              </div>
              {optionalFields.length > 0 && (
                <div className="mt-4">
                  <p className="mb-2 text-xs font-medium text-ink-500">
                    Optional - leave blank to take the default
                  </p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {optionalFields.map((key) => (
                      <Field key={key} label={key} hint={FIELD_HINTS[key]}>
                        <Input
                          type={isSecret(key) ? 'password' : 'text'}
                          value={values[key] ?? ''}
                          onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="mt-4 flex gap-2">
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            {edit.kind === 'dmarc' && (
              <Button loading={testMailbox.isPending} onClick={() => testMailbox.mutate()}>
                Test connection
              </Button>
            )}
            <Button onClick={() => setEdit(null)}>Cancel</Button>
          </div>
          {testResult && (
            <div className="mt-4">
              <Callout tone={testResult.ok ? 'good' : 'bad'}>{testResult.message}</Callout>
            </div>
          )}
          {edit.kind === 'dmarc' && (
            <p className="mt-4 text-xs leading-relaxed text-ink-500">
              Save first, then test - the test reads the stored credentials, not what is typed
              above. Reports are collected every six hours and whenever you press Collect reports
              now on a domain.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

/** Human labels and hints for the credential fields each provider needs. */
const CREDENTIAL_HINTS: Record<string, { label: string; hint?: string; secret?: boolean }> = {
  apiKey: { label: 'API key', secret: true },
  apiSecret: { label: 'API secret', secret: true },
  ownerId: { label: 'Owner ID', hint: 'SendClean dashboard - sent as owner_id in the request body.' },
  token: { label: 'API token', secret: true, hint: 'Sent as token in the request body, not as a header.' },
  smtpUserName: {
    label: 'SMTP user name (needed to send)',
    hint: 'Leave blank, save, then press Test - it lists the SMTP users on your account.',
  },
  webhookBasicAuth: {
    label: 'Webhook basic auth (optional)',
    hint: 'user:pass. Mailjet does not sign webhooks - embed these in the callback URL to secure it.',
  },
  baseUrl: {
    label: 'Base URL (optional)',
    hint: 'Account-specific, e.g. https://api.us1-mta1.sendclean.net/v1.0. Leave blank for the default.',
  },
};

function EspPanel() {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState('mailjet');
  const [name, setName] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [creds, setCreds] = useState<Record<string, string>>({});

  const { data: catalogue } = useQuery({
    queryKey: ['esp-providers'],
    queryFn: () =>
      api.get<{ providers: Array<{ key: string; displayName: string; requiredCredentials: string[] }> }>(
        '/api/esp/providers'
      ),
  });

  const { data } = useQuery({
    queryKey: ['esp-accounts'],
    queryFn: () =>
      api.get<{
        accounts: Array<{
          id: string;
          provider: string;
          name: string;
          status: string;
          credentials: Record<string, string>;
          hasWebhookSecret: boolean;
        }>;
      }>('/api/esp/accounts'),
  });

  const required =
    catalogue?.providers.find((p) => p.key === provider)?.requiredCredentials ?? [];
  // Fields a provider uses but does not need in order to authenticate.
  const optional: Record<string, string[]> = {
    sendclean: ['smtpUserName'],
    mailjet: ['webhookBasicAuth'],
  };
  // baseUrl is never required but several providers are account-specific.
  const fields = [...required, ...(optional[provider] ?? []), 'baseUrl'];
  const complete = required.every((k) => (creds[k] ?? '').trim().length > 0);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/esp/accounts', {
        provider,
        name,
        credentials: Object.fromEntries(
          Object.entries(creds).filter(([, v]) => v.trim().length > 0)
        ),
        webhookSecret: webhookSecret || null,
      }),
    onSuccess: () => {
      setName('');
      setCreds({});
      setWebhookSecret('');
      void queryClient.invalidateQueries({ queryKey: ['esp-accounts'] });
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean; message: string }>('/api/esp/accounts/' + id + '/test'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['esp-accounts'] }),
  });

  const [notice, setNotice] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);
  const refreshAccounts = () => {
    void queryClient.invalidateQueries({ queryKey: ['esp-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['domains'] });
  };

  /** Removes the keys but keeps the account, its webhook URL and its history. */
  const revoke = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean; message: string }>('/api/esp/accounts/' + id + '/credentials'),
    onSuccess: (r) => {
      setNotice({ tone: 'good', text: r.message });
      refreshAccounts();
    },
    onError: (err: Error) => setNotice({ tone: 'bad', text: err.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>('/api/esp/accounts/' + id),
    onSuccess: () => {
      setNotice({ tone: 'good', text: 'Account deleted.' });
      refreshAccounts();
    },
    // A 409 explains exactly what still references the account.
    onError: (err: Error) => setNotice({ tone: 'bad', text: err.message }),
  });

  return (
    <div className="space-y-5">
      <Card title="Sending provider accounts">
        <Table head={['Name', 'Provider', 'Status', 'Credentials', 'Webhook URL', '']}>
          {(data?.accounts ?? []).map((a) => (
            <tr key={a.id}>
              <Td className="font-medium text-ink-900">{a.name}</Td>
              <Td>{a.provider}</Td>
              <Td>
                <Badge tone={a.status === 'active' ? 'good' : 'bad'}>{a.status}</Badge>
              </Td>
              <Td className="font-mono text-[11px] text-ink-500">
                {Object.entries(a.credentials)
                  .map(([k, v]) => k + '=' + v)
                  .join(' ') || 'none'}
              </Td>
              <Td className="max-w-[20rem]">
                {/* The full URL, because this gets pasted into the
                    provider's dashboard - a relative path is useless there. */}
                <CopyableValue value={window.location.origin + '/webhooks/esp/' + a.id} />
              </Td>
              <Td>
                <div className="flex flex-wrap gap-1.5">
                  <Button loading={test.isPending} onClick={() => test.mutate(a.id)}>
                    Test
                  </Button>
                  <Button
                    loading={revoke.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Remove the stored credentials for "' +
                            a.name +
                            '"?\n\nThe account, its webhook URL and its send history are kept, but it is paused and will not send until new keys are added.'
                        )
                      ) {
                        revoke.mutate(a.id);
                      }
                    }}
                  >
                    Revoke keys
                  </Button>
                  <Button
                    variant="danger"
                    loading={remove.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Delete the account "' +
                            a.name +
                            '" entirely?\n\nThis is refused if any domain, mailbox or sent message still references it.'
                        )
                      ) {
                        remove.mutate(a.id);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
        {test.data && (
          <div className="mt-4">
            <Callout tone={test.data.ok ? 'good' : 'bad'}>{test.data.message}</Callout>
          </div>
        )}
        {notice && (
          <div className="mt-4">
            <Callout tone={notice.tone}>{notice.text}</Callout>
          </div>
        )}
      </Card>

      <Card
        title="Add a provider account"
        subtitle="Mailjet, SendClean and Netcore ship out of the box. Adding another provider is one adapter file."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Provider">
            <Select
              value={provider}
              onChange={(e) => {
                setProvider(e.target.value);
                setCreds({});
              }}
            >
              {(catalogue?.providers ?? []).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.displayName}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Account name" hint="Your label for this account.">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>

          <Field label="Webhook secret" hint="Optional. Blank skips signature verification.">
            <Input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
            />
          </Field>

          {/* Credential fields come from the adapter, so each provider asks
              for exactly what it actually authenticates with. */}
          {fields.map((key) => {
            const meta = CREDENTIAL_HINTS[key] ?? { label: key };
            return (
              <Field key={key} label={meta.label} hint={meta.hint}>
                <Input
                  type={meta.secret ? 'password' : 'text'}
                  value={creds[key] ?? ''}
                  onChange={(e) => setCreds({ ...creds, [key]: e.target.value })}
                />
              </Field>
            );
          })}
        </div>

        {required.length === 0 && (
          <div className="mt-4">
            <Callout tone="info">
              This driver needs no credentials. It accepts and logs mail without delivering it.
            </Callout>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!name || !complete}
            onClick={() => create.mutate()}
          >
            Add account
          </Button>
          {!complete && required.length > 0 && (
            <span className="text-xs text-ink-500">
              Needs: {required.filter((k) => !(creds[k] ?? '').trim()).join(', ')}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}

function TrackingPanel() {
  const { data } = useQuery({
    queryKey: ['conversion-snippet'],
    queryFn: () => api.get<{ snippet: string; instructions: string[] }>('/api/analytics/conversion-snippet'),
  });

  if (!data) return <Loading />;

  return (
    <Card
      title="Landing page conversion tracking"
      subtitle="Exact attribution of form submissions back to the message that produced them, without cookies."
    >
      <ul className="mb-4 space-y-1.5">
        {data.instructions.map((line, i) => (
          <li key={i} className="flex gap-2 text-xs leading-relaxed text-ink-600">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ink-400" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      <pre className="max-h-96 overflow-auto rounded-lg bg-ink-900 p-4 text-[11px] leading-relaxed text-ink-100">
        {data.snippet}
      </pre>
      <div className="mt-4">
        <Callout tone="info" title="Where Google Analytics fits">
          If the client will not add the snippet, connect GA4 under Integrations instead. The click
          redirect already appends utm parameters and an <code>eb_mid</code> identifier, so GA4 can
          report conversions per campaign - it is just less precise than the beacon, because it depends
          on GA4 session stitching. Only human-classified clicks are ever forwarded to GA4.
        </Callout>
      </div>
    </Card>
  );
}
